import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import { useParams, Link } from 'react-router-dom';
import { 
    Download, 
    ArrowLeft, 
    Mail, 
    Phone, 
    FileText, 
    Briefcase,
    Calendar
} from 'lucide-react';
import { useGetJobApplicationsQuery, useGetJobByIdQuery } from '../../store/slices/apiSlice';
import { API_BASE_URL } from '../../config/api';

const AdminJobApplications = () => {
    const { jobId } = useParams();
    const { data: appsData, isLoading: isLoadingApps } = useGetJobApplicationsQuery(
        jobId ? { jobId: Number(jobId) } : {}
    );
    const { data: jobData } = useGetJobByIdQuery(Number(jobId), {
        skip: !jobId
    });

    const applications = appsData?.data || [];
    const job = jobData?.data;

    return (
        <div className="min-h-screen bg-gray-50/50 pb-12">
            <div className="p-6 space-y-6 max-w-7xl mx-auto">
                <div className="flex items-center gap-4 mb-8">
                    <Link 
                        to="/admin/jobs"
                        className="p-2 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </Link>
                    <div>
                        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
                            {job ? `Applications: ${job.title}` : 'All Job Applications'}
                        </h2>
                        <p className="text-gray-500 text-sm mt-1">
                            {applications.length} candidate(s) found
                        </p>
                    </div>
                </div>

                {isLoadingApps ? (
                    <div className="flex justify-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                ) : applications.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                        <FileText className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-900">No applications yet</h3>
                        <p className="text-gray-500 mt-2">Check back later for new candidates.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {applications.map((app) => (
                            <div key={app.id} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-all">
                                <div className="flex justify-between items-start mb-4 border-b border-gray-50 pb-4">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">{app.fullName}</h3>
                                        {!jobId && app.job && (
                                            <p className="text-sm text-blue-600 flex items-center gap-1 mt-1 font-medium">
                                                <Briefcase className="w-3 h-3" />
                                                Applied for: {app.job.title}
                                            </p>
                                        )}
                                        <p className="text-xs text-gray-400 flex items-center gap-1 mt-2">
                                            <Calendar className="w-3 h-3" />
                                            {formatDateUtil(app.created_at)}
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="space-y-3 mb-6">
                                    <a 
                                        href={`mailto:${app.email}`}
                                        className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50/50"
                                    >
                                        <div className="bg-blue-50 p-1.5 rounded-md">
                                            <Mail className="w-4 h-4 text-blue-500" />
                                        </div>
                                        {app.email}
                                    </a>
                                    
                                    <a 
                                        href={`tel:${app.phone}`}
                                        className="flex items-center gap-3 text-sm text-gray-600 hover:text-blue-600 transition-colors p-2 rounded-lg hover:bg-blue-50/50"
                                    >
                                        <div className="bg-green-50 p-1.5 rounded-md">
                                            <Phone className="w-4 h-4 text-green-500" />
                                        </div>
                                        {app.phone}
                                    </a>
                                </div>

                                {app.coverLetter && (
                                    <div className="mb-6">
                                        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Cover Letter Preview</h4>
                                        <p className="text-sm text-gray-600 line-clamp-3 bg-gray-50 p-3 rounded-xl italic">
                                            "{app.coverLetter}"
                                        </p>
                                    </div>
                                )}

                                <a 
                                    href={`${API_BASE_URL}/uploads/${app.resumeUrl}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center w-full py-3 bg-gray-50 hover:bg-blue-600 hover:text-white text-blue-600 font-semibold rounded-xl transition-all group-hover:shadow-lg border border-gray-100 hover:border-blue-600 group"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download Resume
                                </a>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminJobApplications;
